import React from "react";
import A4PageFrame from "./A4PageFrame";
import { ROWS_PER_PAGE_LANDSCAPE, ROW_HEIGHT } from "./pageMetrics";
import { fmtNum, fmtDate } from "./formatters";

// Phase 3 (B3): Cost details — mirrors PDF pages 6–13.
//
// Two render modes depending on the API flag `per_invoice_data_available`:
//   - true  → render every invoice line per cost account, with a "Total <name>"
//             subtotal row, then the next account, then "Total général".
//   - false → render a single aggregate line per cost account (fallback shape).
//
// Pagination: ~32 rows per A4 page. Orphan-flagged accounts (cost_number present
// in invoices but not in tbl_cost) are still rendered, prefixed with "(orphan)".
//
// Props
//   data        response from GET /v1/repartition-report/cost-details

// M1.1: switched from portrait to landscape A4 — landscape usable height
// ≈ 658 px vs portrait 1003 px. Each row is now a deterministic 25 px
// (5 + 14 lineHeight + 5 + 1 border) thanks to the explicit `lineHeight`
// pinned on the td style below. Table chrome ≈ 26 px (marginTop + header).
//
// M1.5b: client reported the LAST row on every Coûts page was clipped (one
// invoice missing between pages 6 and 7 of Primerose). Root cause was the
// non-deterministic line-height — fixed by pinning lineHeight on td.
//
// Plaines-6 feedback (3rd pass): the client reported "information cut between
// pages" — the last row on each Coûts page was clipped by the footer. 24 rows
// overran the real usable landscape height. Rows-per-page is now derived from
// the calibrated page geometry (see pageMetrics.js) instead of a hand-tuned
// magic number, so it can never desync from the Preview.js page-number mirror
// and never exceed what fits. Landscape = 22 rows.
const ROWS_PER_PAGE = ROWS_PER_PAGE_LANDSCAPE;

const CostDetails = ({
  data,
  building,
  periodLabel,
  whiteLabelling,
  logoUrl,
  basePageNumber = 1,
  grandTotalPages = 1,
}) => {
  const details = data?.details || [];
  const perInvoice = !!data?.per_invoice_data_available;
  const totals = data?.totals || {};

  const flatRows = perInvoice ? buildInvoiceRows(details, totals) : buildAggregateRows(details, totals);

  const pages = [];
  for (let i = 0; i < flatRows.length; i += ROWS_PER_PAGE) {
    pages.push(flatRows.slice(i, i + ROWS_PER_PAGE));
  }
  if (pages.length === 0) pages.push([]);

  return (
    <>
      {pages.map((rows, pageIdx) => (
        <A4PageFrame
          key={pageIdx}
          building={building}
          periodLabel={periodLabel}
          sectionTitle={`Coûts${pages.length > 1 ? ` (${pageIdx + 1}/${pages.length})` : ""}`}
          whiteLabelling={whiteLabelling}
          logoUrl={logoUrl}
          pageNumber={basePageNumber + pageIdx}
          totalPages={grandTotalPages}
          landscape
        >
          {perInvoice ? (
            <table style={tbl}>
              {/* M1.3: column structure aligned to the standardised report
                  layout confirmed by the client on 10.06.2026 — 11 columns,
                  in this exact order. Added: Nr facture between Date and
                  Libellé. Renamed: "Libellé / Nom créancier" → "Libellé".
                  Widths sum to ~1016 px (landscape A4 ~1059 px usable).
                  Column widths (in order): Compte 46, Désignation 160, Date 66,
                  Nr facture 82, Libellé 260, Quantité 56, Prix HT 56,
                  Montant HT 84, TVA % 46, TVA 76, Montant TTC 84. (Désignation
                  widened from 110 in 2025 to stop clipping account names.)
                  NOTE: <colgroup> must contain ONLY <col> elements — no comments
                  or whitespace text nodes between them (React forbids it). */}
              {/* 2025 client feedback ("cutted information"): Désignation was
                  110 px and clipped account names like "Frais de nettoyage
                  extérieur" / "Système de communication". Widened to 160 px and
                  the numeric columns trimmed to compensate (kept within the
                  ~1059 px landscape usable width). Libellé stays 260 px. Row
                  height stays fixed (nowrap) so pagination remains deterministic. */}
              {/* Étang A feedback 2026-07-17: per-account Quantité totals reach
                  the millions (Chauffage 3'743'476 kWh), which need ~62 px at
                  fontSize 10 + tabular-nums. Old 56 px column ellipsized to
                  "3'743'4…". Widened Quantité 56→72 (+16); Description 1
                  260→244 (-16) — text columns ellipsize gracefully. Row total
                  stays 1016 px inside the ~1059 px landscape band. */}
              <colgroup>
                <col style={{ width: 46 }} />
                <col style={{ width: 160 }} />
                <col style={{ width: 66 }} />
                <col style={{ width: 82 }} />
                <col style={{ width: 244 }} />
                <col style={{ width: 72 }} />
                <col style={{ width: 56 }} />
                <col style={{ width: 84 }} />
                <col style={{ width: 46 }} />
                <col style={{ width: 76 }} />
                <col style={{ width: 84 }} />
              </colgroup>
              <thead>
                <tr style={{ borderBottom: "1px solid #00033F" }}>
                  <th style={th}>Compte</th>
                  <th style={th}>Désignation</th>
                  <th style={th}>Date</th>
                  <th style={th}>Nr facture</th>
                  <th style={th}>Description 1</th>
                  <th style={thNum}>Quantité</th>
                  <th style={thNum}>Prix HT</th>
                  <th style={thNum}>Montant HT</th>
                  <th style={thNum}>TVA %</th>
                  <th style={thNum}>TVA</th>
                  <th style={thNum}>Montant TTC</th>
                </tr>
              </thead>
              <tbody>{rows.map((r, i) => renderInvoiceRow(r, i))}</tbody>
            </table>
          ) : (
            <table style={tbl}>
              <thead>
                <tr style={{ borderBottom: "1px solid #00033F" }}>
                  <th style={th}>Compte</th>
                  <th style={th}>Désignation</th>
                  <th style={th}>Clé</th>
                  <th style={thNum}>Montant HT</th>
                  <th style={thNum}>Montant TTC</th>
                </tr>
              </thead>
              <tbody>{rows.map((r, i) => renderAggregateRow(r, i))}</tbody>
            </table>
          )}
        </A4PageFrame>
      ))}
    </>
  );
};

// Per-invoice mode: emit one row per invoice, a subtotal row per account, and
// a grand total at the end.
function buildInvoiceRows(details, totals) {
  const out = [];
  for (const acct of details) {
    for (const inv of acct.invoices || []) {
      out.push({ kind: "invoice", acct, inv });
    }
    out.push({ kind: "subtotal", acct });
  }
  out.push({ kind: "grand", totals });
  return out;
}

function buildAggregateRows(details, totals) {
  const out = details.map((acct) => ({ kind: "aggregate", acct }));
  out.push({ kind: "grand", totals });
  return out;
}

function renderInvoiceRow(r, i) {
  if (r.kind === "subtotal") {
    // Phase 3 fix: example PDF's "Total <cost>" lines show BOTH HT and TTC.
    // The backend only returns ht/ttc on the subtotal, so TVA = ttc - ht.
    const ht = Number(r.acct.subtotal?.ht || 0);
    const ttc = Number(r.acct.subtotal?.ttc || 0);
    const tva = ttc - ht;
    // M1.1 client feedback: add a Quantité total per cost account. Computed
    // client-side from each invoice's quantity field (backend doesn't return a
    // pre-summed value). Quantities WITHIN one cost account share a unit
    // (e.g. all m³ for "Eau", all kWh for "Electricité"), so summing is safe.
    // Across accounts the units differ — that's why the grand total below
    // does NOT show a Quantité total.
    const quantitySum = (r.acct.invoices || []).reduce(
      (s, inv) => s + (Number(inv.quantity) || 0),
      0,
    );
    // M1.3 11-col per-invoice layout:
    //   colSpan=5 covers Compte+Désignation+Date+Nr facture+Libellé
    //   then: Quantité (sum), blank Prix HT, HT total, blank TVA%,
    //         TVA total, TTC total = 6 cells. 5 + 6 = 11 ✓
    return (
      <tr key={i} style={{ fontWeight: 700, borderTop: "1px solid rgba(0,3,63,0.2)" }}>
        <td style={td} colSpan={5}>
          {r.acct.orphan ? "(orphan) " : ""}Total {r.acct.name || r.acct.cost}
        </td>
        <td style={tdNum}>{quantitySum > 0 ? fmtNum(quantitySum, { decimals: 0 }) : ""}</td>
        <td style={td} />
        <td style={tdNum}>{fmtNum(ht)}</td>
        <td style={td} />
        <td style={tdNum}>{fmtNum(tva)}</td>
        <td style={tdNum}>{fmtNum(ttc)}</td>
      </tr>
    );
  }
  if (r.kind === "grand") {
    // 11-col grand row: colSpan=7 covers Compte through Prix HT, then HT,
    // colSpan=2 (TVA% + TVA), TTC. 7 + 1 + 2 + 1 = 11 ✓
    return (
      <tr key={i} style={{ fontWeight: 700, borderTop: "2px solid #00033F" }}>
        <td style={td} colSpan={7}>Total général</td>
        <td style={tdNum}>{fmtNum(r.totals.ht)}</td>
        <td style={td} colSpan={2} />
        <td style={tdNum}>{fmtNum(r.totals.ttc)}</td>
      </tr>
    );
  }
  // invoice row — M1.3 11-col layout with Nr facture between Date and Libellé.
  const { acct, inv } = r;
  return (
    <tr key={i}>
      <td style={td}>{acct.cost}</td>
      <td style={td}>{acct.name || ""}</td>
      <td style={td}>{fmtDate(inv.date)}</td>
      <td style={td}>{inv.invoice_number || ""}</td>
      <td style={td}>{inv.libelle || ""}</td>
      <td style={tdNum}>{inv.quantity != null ? fmtNum(inv.quantity, { decimals: 0 }) : ""}</td>
      <td style={tdNum}>{inv.price_ht != null ? fmtNum(inv.price_ht, { decimals: 4 }) : ""}</td>
      <td style={tdNum}>{fmtNum(inv.amount_ht)}</td>
      <td style={tdNum}>{inv.tva_pct != null ? fmtNum(inv.tva_pct) : ""}</td>
      <td style={tdNum}>{inv.tva != null ? fmtNum(inv.tva) : ""}</td>
      <td style={tdNum}>{fmtNum(inv.amount_ttc)}</td>
    </tr>
  );
}

function renderAggregateRow(r, i) {
  if (r.kind === "grand") {
    return (
      <tr key={i} style={{ fontWeight: 700, borderTop: "2px solid #00033F" }}>
        <td style={td} colSpan={3}>Total général</td>
        <td style={tdNum}>{fmtNum(r.totals.ht)}</td>
        <td style={tdNum}>{fmtNum(r.totals.ttc)}</td>
      </tr>
    );
  }
  const { acct } = r;
  return (
    <tr key={i}>
      <td style={td}>{acct.cost}</td>
      <td style={td}>{acct.name || ""}</td>
      <td style={td}>{acct.type || ""}</td>
      <td style={tdNum}>{fmtNum(acct.subtotal?.ht)}</td>
      <td style={tdNum}>{fmtNum(acct.subtotal?.ttc)}</td>
    </tr>
  );
}

// M1.2: tableLayout fixed so the explicit <colgroup> widths in the JSX
// actually apply. Without this, a long Libellé column forces other columns
// to compress and the row wraps to 2 lines — that's how the client saw
// "lines cut between pages".
// marginBottom:0 overrides Bootstrap Reboot's global `table{margin-bottom:1rem}`
// which otherwise eats 16px of the fixed content region (part of the recurring
// last-row-clip root cause — see pageMetrics.js).
const tbl = { width: "100%", borderCollapse: "collapse", marginTop: 4, marginBottom: 0, tableLayout: "fixed" };
// M1.1: bumped from 8.5/8pt to 11/10pt per client feedback.
const th = { padding: "3px 4px", fontWeight: 600, textAlign: "left", fontSize: 11 };
const thNum = { ...th, textAlign: "right" };
// M1.5b: explicit `lineHeight` + larger vertical padding so row heights are
// (a) deterministic regardless of PP Neue Montreal's intrinsic line metrics
// — that was the trailing-row clipping bug — and (b) visually comfortable.
// Row total = 5 + 14 + 5 + 1(border) = 25 px. 22 rows × 25 + 26 px chrome
// = 576 px in the 658 px landscape content area → ~87 % page fill (no more
// large empty band at the bottom) and ~13 % safety margin.
const td = {
  // Row height is PINNED so every row — invoice, bold subtotal, grand total — is
  // exactly ROW_HEIGHT.landscape px. Without this the bold/bordered rows render
  // ~1 px taller than plain rows and the cumulative drift used to push the last
  // row under the footer (the recurring "information cut between pages" bug).
  // boxSizing:border-box makes `height` include the padding + border.
  boxSizing: "border-box",
  height: ROW_HEIGHT.landscape,
  padding: "5px 4px",
  fontSize: 10,
  lineHeight: "14px",
  borderBottom: "1px solid rgba(0,3,63,0.05)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const tdNum = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };

export default CostDetails;
